import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isValidTelefone, removeTelefoneMask } from '@/shared/utils/contato';
import type {
  AcordoExistenteInfo,
  BatchNegociacaoState,
  BatchStep,
  MapeamentoColunas,
  NegociacaoRow,
} from '../types/batch.types';
import {
  getCpfCnpjFromMetadata,
  getNomeFromMetadata,
  getWhatsappFromMetadata,
} from '../utils/metadataFallback';
import {
  atualizarItemNegociacao,
  discardAcordosExistentesNegociacao,
  downloadModeloCsvNegociacao,
  fetchLoteItemsNegociacao,
  obterItemParaEdicaoNegociacao,
  uploadCsvNegociacao,
} from './actions';

const validLikeStatuses = new Set<string>(['valid', 'PENDING', 'VALIDATED']);

/**
 * Status cujo `message` NÃO é erro de planilha: os válidos (mensagem de sucesso) e o veredito do
 * porteiro, que é decisão do disparo e aparece na coluna Entrega — não na validação da planilha.
 */
const STATUS_SEM_ERRO_DE_LINHA = new Set<string>([
  ...validLikeStatuses,
  'PROCESSING',
  'VERIFICACAO_REPROVADA',
]);

function applyWhatsappValidation(input: {
  whatsappRaw: string;
  status: string;
  errors: string[];
}): { whatsapp: string; status: string; errors: string[]; invalidatedFromValid: boolean } {
  const digits = removeTelefoneMask(input.whatsappRaw);
  const hasWhatsappError = input.errors.some((e) => e.toLowerCase().includes('whatsapp'));

  if (!digits) {
    const nextErrors = hasWhatsappError
      ? input.errors
      : [...input.errors, 'WhatsApp é obrigatório'];
    return {
      whatsapp: '(vazio)',
      status: 'invalid',
      errors: nextErrors,
      invalidatedFromValid: validLikeStatuses.has(input.status),
    };
  }

  if (!isValidTelefone(input.whatsappRaw)) {
    const nextErrors = hasWhatsappError ? input.errors : [...input.errors, 'WhatsApp inválido'];
    return {
      whatsapp: input.whatsappRaw,
      status: 'invalid',
      errors: nextErrors,
      invalidatedFromValid: validLikeStatuses.has(input.status),
    };
  }

  return {
    whatsapp: input.whatsappRaw,
    status: input.status,
    errors: input.errors,
    invalidatedFromValid: false,
  };
}

const DEFAULT_PAGE_SIZE = 20;

function computeTotals(
  rows: NegociacaoRow[],
  previous?: BatchNegociacaoState['totals'],
): BatchNegociacaoState['totals'] {
  const counts = {
    valid: 0,
    invalid: 0,
    acordoFechado: 0,
    acordoEmAndamento: 0,
    bloqueioTemporario: 0,
    bloqueioPermanente: 0,
    duplicadoPlanilha: 0,
  };
  for (const r of rows) {
    switch (r.status) {
      case 'valid':
      case 'PENDING':
        counts.valid++;
        break;
      case 'ACORDO_FECHADO':
        counts.acordoFechado++;
        break;
      case 'ACORDO_EM_ANDAMENTO':
        counts.acordoEmAndamento++;
        break;
      case 'BLOQUEIO_TEMPORARIO':
        counts.bloqueioTemporario++;
        break;
      case 'BLOQUEIO_PERMANENTE':
        counts.bloqueioPermanente++;
        break;
      case 'DUPLICADO_PLANILHA':
        counts.duplicadoPlanilha++;
        break;
      case 'invalid':
      case 'partial':
      case 'ERROR':
      case 'CONFLICT':
      case 'MISSING_PHONE':
      case 'MISSING_VALUE':
      case 'NOT_FOUND':
        counts.invalid++;
        break;
    }
  }
  return {
    total: rows.length,
    processing: previous?.processing,
    ...counts,
  };
}

function extractAcordoExistente(
  metadata: Record<string, unknown>,
): AcordoExistenteInfo | undefined {
  const bloqueioRaw = metadata?.bloqueio;
  if (bloqueioRaw && typeof bloqueioRaw === 'object') {
    const b = bloqueioRaw as Record<string, unknown>;
    const kind = b.kind;
    if (kind === 'BLOQUEIO_TEMPORARIO' || kind === 'BLOQUEIO_PERMANENTE') {
      const tipoEnc = typeof b.tipoEncerramento === 'string' ? b.tipoEncerramento : null;
      const status = typeof b.status === 'string' ? b.status : null;
      const tipoEncerramento =
        kind === 'BLOQUEIO_PERMANENTE'
          ? ((tipoEnc as AcordoExistenteInfo['tipoEncerramento']) ??
            ((status === 'AGREEMENT_CLOSED' || status === 'NO_AGREEMENT' || status === 'CLOSED'
              ? status
              : null) as AcordoExistenteInfo['tipoEncerramento']))
          : null;
      return {
        kind,
        tipoEncerramento,
        numeroNegociacao: typeof b.numeroNegociacao === 'string' ? b.numeroNegociacao : null,
      };
    }
  }
  const raw = metadata?.acordoExistente;
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const kind = r.kind;
  if (kind !== 'FECHADO' && kind !== 'EM_ANDAMENTO') return undefined;
  return {
    kind,
    numeroNegociacao: typeof r.numeroNegociacao === 'string' ? r.numeroNegociacao : null,
    overriddenAt: typeof r.overriddenAt === 'string' ? r.overriddenAt : undefined,
  };
}

const initialState: BatchNegociacaoState = {
  currentStep: 'agent',
  selectedAgentId: null,
  batchName: '',
  uploadedFile: null,
  usarMapeamento: false,
  colunasDisponiveis: [],
  mapeamento: null,
  rows: [],
  totals: {
    total: 0,
    valid: 0,
    invalid: 0,
    acordoFechado: 0,
    acordoEmAndamento: 0,
    bloqueioTemporario: 0,
    bloqueioPermanente: 0,
    duplicadoPlanilha: 0,
  },
  pagination: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0,
  },
  scheduledDate: undefined,
  scheduledTime: undefined,
  isProcessing: false,
  loteId: null,
  status: null,
  uploadError: null,
  preValidationIssues: null,
  preValidationHasCritical: false,
  preValidationTotalRows: null,
  isDownloadingTemplate: false,
  itemEditData: null,
  isLoadingItemEdit: false,
  isLoadingItems: false,
  itemsError: null,
  isUpdatingItem: false,
  error: null,
};

const batchNegociacaoSlice = createSlice({
  name: 'batchNegociacao',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<BatchStep>) => {
      state.currentStep = action.payload;
    },
    setSelectedAgent: (state, action: PayloadAction<string>) => {
      state.selectedAgentId = action.payload;
    },
    setBatchName: (state, action: PayloadAction<string>) => {
      state.batchName = action.payload;
    },
    setUsarMapeamento: (state, action: PayloadAction<boolean>) => {
      state.usarMapeamento = action.payload;
    },
    setColunasDisponiveis: (state, action: PayloadAction<string[]>) => {
      state.colunasDisponiveis = action.payload;
    },
    setUploadedFile: (state, action: PayloadAction<File | null>) => {
      state.uploadedFile = action.payload;
    },
    setMapeamento: (state, action: PayloadAction<MapeamentoColunas>) => {
      state.mapeamento = action.payload;
    },
    setRows: (state, action: PayloadAction<NegociacaoRow[]>) => {
      state.rows = action.payload;
      state.totals = computeTotals(action.payload, state.totals);
    },
    updateRow: (state, action: PayloadAction<{ rowId: string; data: Partial<NegociacaoRow> }>) => {
      const index = state.rows.findIndex((r) => r.id === action.payload.rowId);
      if (index !== -1) {
        state.rows[index] = { ...state.rows[index], ...action.payload.data };
        state.totals = computeTotals(state.rows, state.totals);
      }
    },
    removeRow: (state, action: PayloadAction<string>) => {
      state.rows = state.rows.filter((r) => r.id !== action.payload);
      state.totals = computeTotals(state.rows, state.totals);
    },
    setSchedule: (state, action: PayloadAction<{ date?: string; time?: string }>) => {
      state.scheduledDate = action.payload.date;
      state.scheduledTime = action.payload.time;
    },
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    setLoteId: (state, action: PayloadAction<string | null>) => {
      state.loteId = action.payload;
    },
    setUploadError: (state, action: PayloadAction<string | null>) => {
      state.uploadError = action.payload;
    },
    clearPreValidation: (state) => {
      state.preValidationIssues = null;
      state.preValidationHasCritical = false;
      state.preValidationTotalRows = null;
    },
    clearItemEditData: (state) => {
      state.itemEditData = null;
      state.error = null;
    },
    setBatchPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = Math.max(1, action.payload);
    },
    setBatchPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = Math.max(1, action.payload);
      state.pagination.page = 1;
    },
    resetBatch: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadModeloCsvNegociacao.pending, (state) => {
        state.isDownloadingTemplate = true;
      })
      .addCase(downloadModeloCsvNegociacao.fulfilled, (state) => {
        state.isDownloadingTemplate = false;
      })
      .addCase(downloadModeloCsvNegociacao.rejected, (state) => {
        state.isDownloadingTemplate = false;
      })
      .addCase(uploadCsvNegociacao.pending, (state) => {
        state.isProcessing = true;
        state.uploadError = null;
        state.preValidationIssues = null;
        state.preValidationHasCritical = false;
        state.preValidationTotalRows = null;
      })
      .addCase(uploadCsvNegociacao.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.loteId = action.payload.loteId;
      })
      .addCase(uploadCsvNegociacao.rejected, (state, action) => {
        state.isProcessing = false;
        const payload = action.payload;
        state.uploadError = payload?.message || 'Erro no upload';
        if (payload?.code === 'SPREADSHEET_VALIDATION_FAILED') {
          state.preValidationIssues = payload.issues ?? [];
          state.preValidationHasCritical = payload.hasCritical ?? true;
          state.preValidationTotalRows = payload.totalRows ?? null;
        } else {
          state.preValidationIssues = null;
          state.preValidationHasCritical = false;
          state.preValidationTotalRows = null;
        }
      })
      .addCase(fetchLoteItemsNegociacao.pending, (state) => {
        state.isLoadingItems = true;
        state.itemsError = null;
      })
      .addCase(fetchLoteItemsNegociacao.rejected, (state, action) => {
        state.isLoadingItems = false;
        state.itemsError = action.payload || 'Não foi possível carregar as linhas do lote.';
      })
      .addCase(fetchLoteItemsNegociacao.fulfilled, (state, action) => {
        state.isLoadingItems = false;
        state.itemsError = null;
        let invalidatedFromValidCount = 0;

        state.rows = action.payload.items.map((it) => {
          const baseStatus = String(it.status);
          const itemErrors = it.item.erros && it.item.erros.length > 0 ? it.item.erros : [];
          // O `message` do lote item vira erro de linha só quando ele é MESMO um erro de
          // planilha. Em `VERIFICACAO_REPROVADA` ele carrega o veredito do porteiro ("número
          // bloqueado por falha anterior"), que é do disparo — promovê-lo aqui fazia a tela de
          // validação pintar a linha de vermelho com uma decisão que ela não tomou. E em status
          // válido o `message` é "Validado com sucesso", que nunca deveria ter contado como erro.
          // A checagem de `it.message` fica na mesma expressão para o TS estreitar o
          // `string | undefined` — extraída para um booleano, a narrowing se perde.
          const messageErrors =
            !STATUS_SEM_ERRO_DE_LINHA.has(baseStatus) && itemErrors.length === 0 && it.message
              ? [it.message]
              : [];
          const baseErrors = itemErrors.length > 0 ? itemErrors : messageErrors;
          const metadata = it.item.metadata || {};
          const nomeRaw = it.item.contato.nome || getNomeFromMetadata(metadata) || '';
          const cpfCnpjRaw = it.item.contato.cpfCnpj || getCpfCnpjFromMetadata(metadata) || '';
          // O `[0]` daqui era o ÚLTIMO ponto que ainda descartava os outros números do
          // devedor: o back já devolve `contato.telefones` inteiro. `whatsappRaw` segue sendo o
          // primeiro (é o que vira `counterpartyPhone`), e a coluna passa a exibir todos.
          const telefonesDoItem = (it.item.contato.telefones ?? []).filter(
            (numero): numero is string => typeof numero === 'string' && numero.length > 0,
          );
          const whatsappRaw =
            it.item.whatsapp ||
            it.item.telefone ||
            it.item.contato.whatsapp ||
            it.item.contato.telefone ||
            telefonesDoItem[0] ||
            getWhatsappFromMetadata(metadata) ||
            '';
          const whatsappValidation = applyWhatsappValidation({
            whatsappRaw,
            status: baseStatus,
            errors: baseErrors,
          });

          if (whatsappValidation.invalidatedFromValid) {
            invalidatedFromValidCount += 1;
          }

          return {
            id: it.id,
            itemId: it.itemId,
            linhaNumero: it.item.linhaNumero,
            nome: nomeRaw,
            cpfCnpj: cpfCnpjRaw,
            whatsapp: whatsappValidation.whatsapp,
            telefones: telefonesDoItem.length > 0 ? telefonesDoItem : [whatsappValidation.whatsapp],
            customFields: metadata,
            status: whatsappValidation.status as NegociacaoRow['status'],
            errors: whatsappValidation.errors,
            warnings: it.item.warnings ?? [],
            acordoExistente: extractAcordoExistente(metadata),
          };
        });

        const t = action.payload.totais;
        const pending = Number(t.pending ?? 0);
        const validLike = t.valid + pending + t.processing;
        const valid = Math.max(0, validLike - invalidatedFromValidCount);
        const invalid =
          Number(t.error ?? 0) +
          Number(t.existing ?? 0) +
          Number(t.notFound ?? 0) +
          Number(t.missingPhone ?? 0) +
          Number(t.missingValue ?? 0) +
          Number(t.conflict ?? 0) +
          invalidatedFromValidCount;
        state.totals = {
          total: t.total,
          valid,
          invalid,
          processing: t.processing,
          acordoFechado: Number(t.acordoFechado ?? 0),
          acordoEmAndamento: Number(t.acordoEmAndamento ?? 0),
          bloqueioTemporario: Number(t.bloqueioTemporario ?? 0),
          bloqueioPermanente: Number(t.bloqueioPermanente ?? 0),
          duplicadoPlanilha: Number(t.duplicadoPlanilha ?? 0),
          nomeDivergente: t.nomeDivergente === undefined ? undefined : Number(t.nomeDivergente),
        };
        state.pagination.totalItems = action.payload.total;
      })
      .addCase(obterItemParaEdicaoNegociacao.pending, (state) => {
        state.isLoadingItemEdit = true;
        state.error = null;
      })
      .addCase(obterItemParaEdicaoNegociacao.fulfilled, (state, action) => {
        state.isLoadingItemEdit = false;
        state.itemEditData = action.payload;
      })
      .addCase(obterItemParaEdicaoNegociacao.rejected, (state, action) => {
        state.isLoadingItemEdit = false;
        state.error = action.payload || 'Erro ao carregar item';
      })
      .addCase(atualizarItemNegociacao.pending, (state) => {
        state.isUpdatingItem = true;
        state.error = null;
      })
      .addCase(atualizarItemNegociacao.fulfilled, (state, action) => {
        state.isUpdatingItem = false;
        const { itemId, input } = action.meta.arg;
        const rowIndex = state.rows.findIndex((r) => r.id === itemId || r.itemId === itemId);
        if (rowIndex === -1) {
          return;
        }

        const currentRow = state.rows[rowIndex];

        if (input.nome !== undefined) {
          currentRow.nome = input.nome;
        }
        if (input.documento !== undefined) {
          currentRow.cpfCnpj = input.documento;
        }
        // A linha guarda a lista inteira (é ela que a coluna WhatsApp exibe em chips) e o
        // principal separado. Atualizar só o `whatsapp` deixava os chips com os números antigos
        // até o próximo fetch — e, quando o usuário removia um número no drawer, ele continuava
        // aparecendo na tabela.
        const nextPhones = input.contato?.telefones;
        if (nextPhones !== undefined) {
          currentRow.telefones = nextPhones;
          currentRow.whatsapp = nextPhones[0] ?? '';
        }
        if (input.metadata) {
          currentRow.customFields = input.metadata as Record<string, unknown>;
        }
      })
      .addCase(atualizarItemNegociacao.rejected, (state, action) => {
        state.isUpdatingItem = false;
        state.error = action.payload || 'Erro ao salvar item';
      })
      .addCase(discardAcordosExistentesNegociacao.fulfilled, (state) => {
        state.rows = state.rows.filter(
          (r) =>
            r.status !== 'ACORDO_FECHADO' &&
            r.status !== 'ACORDO_EM_ANDAMENTO' &&
            r.status !== 'BLOQUEIO_TEMPORARIO' &&
            r.status !== 'BLOQUEIO_PERMANENTE' &&
            r.status !== 'DUPLICADO_PLANILHA',
        );
        state.totals = {
          ...state.totals,
          acordoFechado: 0,
          acordoEmAndamento: 0,
          bloqueioTemporario: 0,
          bloqueioPermanente: 0,
          duplicadoPlanilha: 0,
        };
      });
  },
});

export const {
  setCurrentStep,
  setSelectedAgent,
  setUsarMapeamento,
  setColunasDisponiveis,
  setUploadedFile,
  setMapeamento,
  updateRow,
  setSchedule,
  setLoteId,
  clearItemEditData,
  clearPreValidation,
  setBatchPage,
  setBatchPageSize,
  resetBatch,
} = batchNegociacaoSlice.actions;

export default batchNegociacaoSlice.reducer;
